import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { cartLineToItem, unitPrice, useCart } from '../store/cart';
import { COUPONS, CURRENCY, FREE_SHIPPING_FROM, byId } from '../data/catalog';
import { Button, Card, Money, inputCls } from '../components/ui';
import { useTitle } from '../components/Layout';

export function Cart() {
  useTitle('Koszyk');
  const cart = useCart();
  const nav = useNavigate();
  const [code, setCode] = useState('');
  const [couponError, setCouponError] = useState('');

  // view_cart — rekomendowane zdarzenie GA4, raz na OTWARCIE strony koszyka
  // (nie przy każdej zmianie ilości czy usunięciu pozycji w trakcie — to,
  // w odróżnieniu od zmiany wariantu na karcie produktu, nie jest "inny
  // widok", tylko edycja tego samego). Dokładnie jeden fizyczny mount tej
  // strony = dokładnie jedno zdarzenie, więc zwykła flaga `useRef(false)`
  // wystarcza — w przeciwieństwie do view_item nie ma tu scenariusza, w
  // którym ten sam zamontowany komponent powinien wysłać zdarzenie
  // ponownie bez odświeżenia. Nadal chroni przed podwójnym wywołaniem
  // efektu przez <StrictMode> przy montowaniu.
  //
  // Tylko pola udokumentowane dla view_cart: currency, value, items.
  // Świadomie pomijam event-scoped `coupon` — jest w specyfikacji GA4 dla
  // begin_checkout/purchase/refund, ale nie ma go w oficjalnej liście
  // parametrów dla view_cart, więc się tu nie znajdzie.
  const fired = useRef(false);
  useEffect(() => {
    if (fired.current || cart.lines.length === 0) return;
    fired.current = true;

    const items = cart.lines.map(cartLineToItem).filter((item): item is NonNullable<typeof item> => item !== null);

    if (items.length === 0) return;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: 'view_cart',
      ecommerce: {
        currency: CURRENCY,
        value: cart.subtotal,
        items,
      },
    });
    // Celowo pusta tablica zależności — to zdarzenie ma odzwierciedlać stan
    // koszyka w momencie WEJŚCIA na stronę, nie każdą późniejszą zmianę.
    // `fired` i tak dopuszcza tylko jedno wykonanie na mount.
  }, []);

  if (cart.lines.length === 0) {
    return (
      <div className="py-20 text-center">
        <p className="text-4xl">🛒</p>
        <p className="mt-3 text-slate-400">Twój koszyk jest pusty.</p>
        <Link to="/katalog" className="mt-3 inline-block text-[#9ec5ff] underline">
          Przejdź do katalogu
        </Link>
      </div>
    );
  }

  const brakuje = FREE_SHIPPING_FROM - cart.subtotal;

  return (
    <>
      <h1 className="mb-4 text-xl font-semibold text-white">Koszyk ({cart.count})</h1>

      <div className="grid gap-5 lg:grid-cols-[1fr_340px]">
        <div className="space-y-2">
          {cart.lines.map((l) => {
            const p = byId(l.productId);
            const v = p?.variants.find((x) => x.sku === l.sku);
            if (!p || !v) return null;
            return (
              <Card key={l.sku} className="flex flex-wrap items-center gap-3 p-3">
                <Link
                  to={`/produkt/${p.slug}`}
                  className="flex h-16 w-16 shrink-0 items-center justify-center rounded-lg text-2xl"
                  style={{ background: `${p.color}18` }}
                >
                  {p.emoji}
                </Link>
                <div className="min-w-[160px] flex-1">
                  <Link to={`/produkt/${p.slug}`} className="text-[13.5px] font-medium text-slate-100 hover:underline">
                    {p.name}
                  </Link>
                  <p className="text-[11.5px] text-slate-500">{v.label}</p>
                  <p className="text-[11px] text-slate-600">{v.sku}</p>
                </div>
                <input
                  type="number"
                  min={1}
                  value={l.qty}
                  onChange={(e) => cart.setQty(l.sku, Number(e.target.value))}
                  aria-label={`Ilość: ${p.name}`}
                  className="w-16 rounded-lg border border-[#26304d] bg-[#0b1020] px-2 py-1.5 text-sm text-white outline-none"
                />
                <div className="w-28 text-right text-[13.5px] font-medium text-white">
                  <Money v={unitPrice(l) * l.qty} />
                </div>
                <button
                  onClick={() => {
                    // remove_from_cart — rekomendowane zdarzenie GA4. `quantity`
                    // to CAŁA usuwana ilość (przycisk ✕ zdejmuje linię w
                    // całości), `value` to cena razy ta ilość.
                    //
                    // Uwaga: zmniejszenie ilości przez pole liczbowe obok
                    // (l.qty > 1 → mniej) NIE wysyła tego zdarzenia — to
                    // częściowe usunięcie i osobna decyzja pomiarowa
                    // (wymagałaby śledzenia poprzedniej wartości, żeby
                    // policzyć deltę). Łapiemy tylko pełne usunięcie pozycji.
                    const item = cartLineToItem(l);
                    cart.remove(l.sku);
                    if (item) {
                      window.dataLayer = window.dataLayer || [];
                      window.dataLayer.push({ ecommerce: null });
                      window.dataLayer.push({
                        event: 'remove_from_cart',
                        ecommerce: {
                          currency: CURRENCY,
                          value: Math.round(item.price * item.quantity * 100) / 100,
                          items: [item],
                        },
                      });
                    }
                  }}
                  className="text-slate-600 hover:text-[#ff5c72]"
                  aria-label={`Usuń: ${p.name}`}
                >
                  ✕
                </button>
              </Card>
            );
          })}
        </div>

        <div className="space-y-3">
          <Card className="p-4">
            <h2 className="mb-3 text-sm font-semibold text-white">Podsumowanie</h2>
            <div className="space-y-1.5 text-[13px]">
              <Row label="Wartość produktów" value={<Money v={cart.subtotal} />} />
              {cart.discount > 0 && (
                <Row
                  label={`Rabat (${cart.coupon})`}
                  value={
                    <span className="text-[#7ef0d0]">
                      −<Money v={cart.discount} />
                    </span>
                  }
                />
              )}
              <Row label="w tym VAT 23%" value={<Money v={cart.tax} />} muted />
              <div className="my-2 border-t border-[#26304d]" />
              <Row
                label="Do zapłaty (bez dostawy)"
                value={
                  <span className="text-base font-semibold text-white">
                    <Money v={cart.subtotal - cart.discount} />
                  </span>
                }
              />
            </div>

            {brakuje > 0 && (
              <p className="mt-3 rounded-lg bg-[#4f8cff]/10 p-2.5 text-[12px] text-[#bcd4ff]">
                Do darmowej dostawy brakuje <Money v={brakuje} />
              </p>
            )}

            <div className="mt-4">
              <label className="mb-1 block text-xs text-slate-400" htmlFor="kupon">
                Kod rabatowy
              </label>
              <div className="flex gap-2">
                <input
                  id="kupon"
                  className={inputCls}
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="np. LATO10"
                />
                <Button
                  variant="ghost"
                  onClick={() => {
                    const ok = cart.applyCoupon(code);
                    setCouponError(ok ? '' : 'Nieprawidłowy kod rabatowy.');
                    if (ok) setCode('');
                  }}
                >
                  Zastosuj
                </Button>
              </div>
              {couponError && <p className="mt-1.5 text-[11.5px] text-[#ffb0bb]">{couponError}</p>}
              {cart.coupon && (
                <p className="mt-1.5 text-[11.5px] text-[#7ef0d0]">
                  {cart.coupon} — {COUPONS[cart.coupon]?.label}{' '}
                  <button className="ml-1 text-slate-500 underline" onClick={() => cart.applyCoupon(undefined)}>
                    usuń
                  </button>
                </p>
              )}
            </div>

            <Button className="mt-4 w-full py-2.5" onClick={() => nav('/zamowienie')}>
              Przejdź do zamówienia
            </Button>
            <Link to="/katalog" className="mt-2 block text-center text-[12px] text-slate-500 hover:text-slate-300">
              Kontynuuj zakupy
            </Link>
          </Card>
        </div>
      </div>
    </>
  );
}

function Row({ label, value, muted }: { label: string; value: React.ReactNode; muted?: boolean }) {
  return (
    <div className="flex justify-between">
      <span className={muted ? 'text-slate-500' : 'text-slate-400'}>{label}</span>
      <span className={muted ? 'text-slate-500' : 'text-slate-200'}>{value}</span>
    </div>
  );
}
